<template>
<div :class="{
        'VehicleStateEmpty': 1,
        'VehicleStateEmpty--mobileExpanded': mobileExpanded,
        'VehicleStateEmpty--mobileModeVin': mobileMode == 'vin',
        'VehicleStateEmpty--mobileModeMmy': mobileMode == 'mmy',
        'VehicleStateEmpty--variantHeaderOverlap': variant == 'header-overlap',
        'VehicleStateEmpty--variantProductOverlay': variant == 'product-overlay',
        'VehicleStateEmpty--variantCompatibilityChecker': variant == 'compatibility-checker',
        'VehicleStateEmpty--service': variant === 'service'
    }">
    <button
        @click="mobileExpanded = !mobileExpanded"
        class="VehicleStateEmpty__MobileHeader">
        <app-icon
            :icon="iconCar"/>
        <span class="VehicleStateEmpty__MobileTitle">
            {{ $t('collection_vehicle_bar_empty.mobile_title') }}
        </span>
        <app-icon
            :icon="iconChevronUp"
            class="VehicleStateEmpty__MobileHeaderCaret"/>
    </button>
    <div class="VehicleStateEmpty__Container">
        <div v-if="variant !== 'service'" class="VehicleStateEmpty__MobileToggle ">
            <button
                @click="() => setMobileMode('vin')"
                :class="{
                    'VehicleStateEmpty__MobileToggleOption': 1,
                    'VehicleStateEmpty__MobileToggleOption--active': mobileMode == 'vin',
                }">
                <app-icon
                    :icon="mobileMode == 'vin' ? iconRadioSelected : iconRadioUnselected"/>
                {{ $t('collection_vehicle_bar_empty.search_modes.registration_label') }}
            </button>
            <button
                v-if="variant !== 'service'"
                @click="() => setMobileMode('mmy')"
                :class="{
                    'VehicleStateEmpty__MobileToggleOption': 1,
                    'VehicleStateEmpty__MobileToggleOption--active': mobileMode == 'mmy',
                }">
                <app-icon
                    :icon="mobileMode == 'mmy' ? iconRadioSelected : iconRadioUnselected"/>
                {{ $t('collection_vehicle_bar_empty.search_modes.model_label') }}
            </button>
        </div>
        <div class="VehicleStateEmpty__Content">
            <div
                :class="{
                    'VehicleStateEmpty__Section': 1,
                    'VehicleStateEmpty__Section--vin': 1,
                    'VehicleStateEmpty__Section--disabled': !!mmyResult,
                }">
                <div class="VehicleStateEmpty__SectionTitle"
                    v-html="decodeHtml($t('collection_vehicle_bar_empty.vin_search_label'))">
                </div>
                <vin-vehicle-lookup
                    v-if="store === 'de'"
                    v-model="vinResult"
                    :disabled="!!mmyResult"
                    @trigger-search="canSubmit ? onSubmit() : null"/>
                <vin-vehicle-lookup
                    v-else
                    v-model="vinResult"
                    :disabled="!!mmyResult"
                    @trigger-search="canSubmit ? onSubmit() : null"/>
            </div>
            <div
                v-if="variant !== 'service'"
                :class="{
                    'VehicleStateEmpty__Section': 1,
                    'VehicleStateEmpty__Section--separator': 1,
                    'VehicleStateEmpty__Section--disabled': !!(vinResult || mmyResult),
                }">
                {{ $t('collection_vehicle_bar_empty.separator' )}}
            </div>
            <div
                v-if="variant !== 'service'"
                :class="{
                    'VehicleStateEmpty__Section': 1,
                    'VehicleStateEmpty__Section--mmy': 1,
                    'VehicleStateEmpty__Section--disabled': !!vinResult,
                }">
                <div class="VehicleStateEmpty__SectionTitle">
                    {{ $t('collection_vehicle_bar_empty.mmy_search_label') }}
                </div>
                <simple-mmy-vehicle-lookup
                    v-model="mmyResult"
                    ref="mmyLookup"
                    :disabled="!!vinResult"/>
            </div>
        </div>
        <transition name="VehicleStateEmpty_slideWidth">
            <button
                class="VehicleStateEmpty__Submit"
                :disabled="!canSubmit"
                @click="onSubmit">
                {{ $t('collection_vehicle_bar_empty.submit' )}}
            </button>
        </transition>
    </div>
</div>
</template>

<script>
import log from 'salvo-lite/log'
import { components as partindexComponents } from '@scripts/services/partindex'
import { garageService } from '@scripts/services/garageService'

import iconChevronUp from '@icons/icon_chevron_up.svg'
import iconCar from '@icons/icon_car.svg'
import iconRadioSelected from '@icons/icon_radio_selected.svg'
import iconRadioUnselected from '@icons/icon_radio_unselected.svg'

const TAG = 'CollectionVehicleBarEmpty'

export default {
    components: {
        ...partindexComponents
    },
    props: {
        variant: String
    },
    data () {
        return {
            iconChevronUp,
            iconCar,
            iconRadioSelected,
            iconRadioUnselected,

            vinResult: null, // VinVehicleDetails|null
            mmyResult: null, // MmyVehicleDetails|null

            mobileExpanded: false,
            mobileMode: 'vin' // 'vin'|'mmy'
        }
    },
    computed: {
        canSubmit () {
            return !!(this.vinResult || this.mmyResult)
        },
        vehicle () {
            return garageService.activeVehicle || null
        },
        store() {
            return window._esc_PartIndexer_config.context.store;
        },
    },
    methods: {
        setMobileMode (mode) {
            if (mode === this.mobileMode) {
                return
            }
            this.mobileMode = mode

            this.vinResult = null
            this.mmyResult = null
            this.$refs.mmyLookup.reset()
        },
        async onSubmit () {
            let result = this.vinResult || this.mmyResult
            this.vinResult = null
            this.mmyResult = null

            log.debug(TAG, 'Setting active', {result})

            await garageService.setActive(result)
        },
        decodeHtml(html) {
            const ta = document.createElement("textarea");
            ta.innerHTML = html;
            return ta.value;
        },
    }
}
</script>

<style lang="scss">
$fontRegular: 'Ford Antenna', Helvetica, Arial, sans-serif;
$fontCondensed: 'Ford Antenna Condensed', 'Ford Antenna Cond', Helvetica, Arial, sans-serif;

$mobileBreakpoint: 768px;
$smallDesktopBreakpoint: 1024px;

.VehicleStateEmpty {

    &__Container {
        display: flex;
        box-shadow: 0 10px 10px rgba(0, 0, 0, 0.1);
        background-color: #102b4e;
    }

    &__Content {
        flex-grow: 1;

        padding: 20px 40px;

        color: white;

        display: flex;
        align-items: center;
        justify-content: stretch;
    }

    &__Section {
        flex-grow: 1;

        &--vin {
            flex-grow: 0;
            flex-basis: 290px;
        }
        &--mmy {
            flex-basis: 60%;
        }

        &--disabled {
            opacity: 0.5;
        }

        &--separator {
            flex-grow: 0;
            flex-shrink: 0;

            display: flex;
            align-items: center;
            justify-content: center;

            height: 72px;
            align-self: flex-end;

            padding: 10px;

            text-transform: uppercase;
            font-size: 12px;
            font-weight: 400;
            letter-spacing: normal;
        }
    }

    &__SectionTitle {
        margin-bottom: 10px;

        font-weight: 700;
        font-size: 12px;
        line-height: 26px;
        letter-spacing: normal;

        text-transform: uppercase;
    }

    &__Submit {
        flex-shrink: 0;

        appearance: none;
        border: none;
        outline: none;
        background: none;
        padding: 0;

        width: 130px;
        background: white;
        border-radius: 3px;
        box-shadow: 0 10px 10px rgba(0, 0, 0, 0.1), 0 20px 20px rgba(0, 0, 0, 0.1);

        text-align: center;
        color: #102b4e;
        font-family: $fontCondensed;
        font-weight: 300;
        letter-spacing: 4px;
        text-transform: uppercase;

        &:disabled {
            opacity: 1;
            color: lighten(desaturate(#102b4e, 80%), 30%);
            //background: #eee;
        }
    }

    &_slideWidth {
        $animScale: .3s;

        &-enter-active,
        &-leave-active {
            overflow: hidden;
            width: 130px;
            color: #102b4e;
            transition: width $animScale linear #{$animScale * 0.333}, color #{$animScale * 0.333};
        }
        &-enter-active {
            transition: width $animScale, color #{$animScale * 0.333} linear #{$animScale * 0.666};
        }

        &-enter,
            &-leave-to {
            color: transparent;
            width: 0px;
        }
    }

    &__MobileHeader {
        width: 100%;

        appearance: none;
        border: none;
        background: none;
        outline: none;

        background-color: #102b4e;
        padding: 16px;
        position: relative;

        display: flex;
        align-items: center;
        justify-content: center;

        color: white;
        font-family: $fontCondensed;
        font-size: 16px;
        letter-spacing: 2px;
        text-transform: uppercase;

        display: none;
    }
    &__MobileTitle {
        margin-left: 10px;
        margin-right: 10px;
    }
    &__MobileHeaderCaret {
        position: absolute;
        right: 30px;
        top: 50%;
        transform: translateY(-50%);
    }
    &__MobileToggle {
        padding: 0 28px;
        padding-bottom: 5px;

        display: flex;
        align-items: center;
        justify-content: space-around;

        display: none;
    }
    &__MobileToggleOption {
        border: none;
        background: none;
        outline: none;
        appearance: none;
        padding: 0;

        padding: 15px;

        color: #afb7c2;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;

        display: flex;
        align-items: center;

        .PI-AppIcon {
            margin-right: 12px;
        }

        transition: color .2s;
        &--active {
            color: white;
        }
    }

    @media screen and (max-width: $mobileBreakpoint) {
        &__MobileHeader {
            display: flex;
        }
        &__Container {
            display: none;
        }
        &--mobileExpanded {
            .VehicleStateEmpty__MobileHeader {
                border-bottom: 1px solid rgba(255,255,255,0.1);
            }
            .VehicleStateEmpty__MobileHeaderCaret {
                transform: translateY(-50%) rotate(180deg);
            }
            .VehicleStateEmpty__Container {
                display: block;
            }
        }

        &__Content {
            padding: 28px;
            padding-top: 0;
        }
        &__Submit {
            width: 100%;
            padding: 20px 25px;

            border-top-left-radius: 0;
            border-top-right-radius: 0;

            // To prevent background from peeking at corners
            margin-bottom: -5px;
        }

        &__MobileToggle {
            display: flex;
        }
        .VehicleStateEmpty__Section--vin,
        .VehicleStateEmpty__Section--mmy,
        .VehicleStateEmpty__Section--separator {
            display: none;
        }
        &--mobileModeVin {
            .VehicleStateEmpty__Section--vin {
                display: flex;
                flex-grow: 1;
                flex-basis: unset;
            }
        }
        &--mobileModeMmy {
            .VehicleStateEmpty__Section--mmy {
                display: flex;
            }
        }

        .VehicleStateEmpty__SectionTitle {
            display: none;
        }

    }

    // Variant: header-overlap
    // Should be positioned under a large header banner.
    // Overlaps the bottom edge of the header.
    &--variantHeaderOverlap {
        // Horizontal positioning
        margin: 0 auto;
        width: calc(100% - 120px);
        @media screen and (max-width: $smallDesktopBreakpoint) {
            width: calc(100% - 30px);
        }
        @media screen and (max-width: $mobileBreakpoint) {
            width: 100%;
        }

        // Vetical alignment
        margin-top: -90px;
        position: relative;
        z-index: 10;
        @media screen and (max-width: $mobileBreakpoint) {
            // On mobile, attach below the header
            margin-top: 0;
        }
    }

    // Variant: product-overlay
    // Similar to the mobile layout
    // Displays like a dropdown
    &--variantProductOverlay {
        .VehicleStateEmpty__Container {
            flex-direction: row;
            flex-wrap: wrap;

            position: absolute;
            left: 0;
            right: 0;
            z-index: 5;
        }

        // Replicate mobile styling
        @media screen and (min-width: $mobileBreakpoint) {
            .VehicleStateEmpty__MobileHeader {
                display: flex;
                border-bottom: 1px solid transparent;
            }
            .VehicleStateEmpty__Container {
                display: none;
            }

            .VehicleStateEmpty__Content {
                padding: 28px;
                padding-top: 0;
            }
            .VehicleStateEmpty__Submit {
                width: 100%;
                padding: 20px 25px;

                border-top-left-radius: 0;
                border-top-right-radius: 0;

                // To prevent background from peeking at corners
                margin-bottom: -5px;
            }

            .VehicleStateEmpty__MobileToggle {
                display: flex;
            }
            .VehicleStateEmpty__Section--vin,
            .VehicleStateEmpty__Section--mmy,
            .VehicleStateEmpty__Section--separator {
                display: none;
            }
            &.VehicleStateEmpty--mobileModeVin {
                .VehicleStateEmpty__Section--vin {
                    display: flex;
                    flex-grow: 1;
                    flex-basis: unset;
                }
            }
            &.VehicleStateEmpty--mobileModeMmy {
                .VehicleStateEmpty__Section--mmy {
                    display: flex;
                }
            }
            &.VehicleStateEmpty--mobileExpanded {
                .VehicleStateEmpty__MobileHeader {
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                }
                .VehicleStateEmpty__MobileHeaderCaret {
                    transform: translateY(-50%) rotate(180deg);
                }
                .VehicleStateEmpty__Container {
                    display: flex;
                }
            }

            // Adjust for horizontal layout
            .VehicleStateEmpty__MobileHeader {
                justify-content: flex-start;
                font-size: 21px;
            }
            .VehicleStateEmpty__Container {
                flex-direction: row;
                flex-wrap: wrap;

                position: absolute;
                left: 0;
                right: 30px;
                z-index: 5;
            }
            .VehicleStateEmpty__MobileToggle {
                flex-basis: 30%;
                flex-shrink: 0;
                flex-grow: 0;

                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: center;

                border-right: 1px solid transparentize(#979797, 0.2);
            }
            .VehicleStateEmpty__Content {
                flex-basis: 30%;
                flex-grow: 1;
                flex-shrink: 1;

                padding: 24px;
            }
            .VehicleStateEmpty__Submit {
                width: 100%;
                flex-basis: 100%;
            }
            .VehicleStateEmpty__Section {
                flex-direction: column;
            }
        }
    }

    // Variant: compatibility-checker
    // Used on the compatibility section of the product page
    &--variantCompatibilityChecker {
        .VehicleStateEmpty__Container {
            background: none;
            box-shadow: none;
            align-items: center;
        }
        .VehicleStateEmpty__Content {
            padding: 0;
            padding-right: 10px;
        }
        .VehicleStateEmpty__Submit {
            padding: 0;
            height: 40px;
            border-radius: 20px;
            margin-top: 35px;
            font-size: 18px;
            text-transform: none;
            letter-spacing: 1px;
        }
        .PI-VinVehicleLookup__PlateDecorator {
            background-color: #102b4e;
        }

        @media screen and (max-width: $mobileBreakpoint) {
            // Always show dropdown
            .VehicleStateEmpty__Container {
                display: block;
            }
            // Hide header button
            .VehicleStateEmpty__MobileHeader {
                display: none;
            }
        }
    }

    // Variant: service
    // Used on the service plans page
    &--service {
        .VehicleStateEmpty__Content {
            padding-top: 28px;
            display: block;
        }
    }
}
</style>



// WEBPACK FOOTER //
// VehicleStateEmpty.vue?6fb1bdb1